import { EventTrackingAction } from '@opendoor/observability/slim';
import { useObservability } from '../../../helpers/observability';
import { createContext, useCallback, useContext } from 'react';
import { GetBwodListingPropertyInfoQuery } from '__generated__/athena';
import { DEPLOY_ENV } from '../../globals';
import { useAuth } from '@opendoor/auth-fe';
import { getAnonymousId } from '../../shared/trackers';

interface EventData {
  [key: string]: unknown;
}

interface TrackProperty {
  city?: string;
  state?: string;
  postalCode?: string;
  url?: string;
  propertyStatus?: string;
}

// Action names should follow this format
type PropertyEventAction =
  | 'property-view'
  | 'property-favorite'
  | 'property-share'
  | 'property-similar-listings'
  | 'property-purchase-calculator'
  | 'property-images';

export type Property = GetBwodListingPropertyInfoQuery['property']['property'];

interface PropertyAnalyticsProps {
  property?: Property;
}

const initialPropertyContext: PropertyAnalyticsProps = {
  property: undefined,
};

export const PropertyAnalytics = createContext(initialPropertyContext);

export const usePropertyAnalytics = () => useContext(PropertyAnalytics);

export const useTrackProperty = () => {
  const analytics = useObservability();
  const { property } = usePropertyAnalytics();
  const { user } = useAuth();

  const trackProperty = useCallback(
    function <T extends EventData>(
      featureName: PropertyEventAction,
      label?: string,
      additionalContextualData: T = <T>{},
    ) {
      const action = featureName as EventTrackingAction;
      const trackPropertyFields: TrackProperty = {
        ...(property?.city && { city: property?.city }),
        ...(property?.state && { state: property?.state }),
        ...(property?.postalCode && { zip: property?.postalCode }),
        ...(property?.displaySlug && { url: property?.displaySlug }),
        ...(property?.status && { propertyStatus: property?.status }),
        ...(property?.opendoorOwned && { opendoorOwned: property?.opendoorOwned }),
      };
      const properties = {
        ...additionalContextualData,
        ...trackPropertyFields,
        anonymousId: getAnonymousId(),
        ...(user?.customerUuid && { customerId: user.customerUuid }),
      };
      // Filter out any keys where the value is empty
      const payload = Object.keys(properties)
        .filter((key) => properties[key])
        .reduce((obj: { [key: string]: unknown }, key) => {
          obj[key] = properties[key];
          return obj;
        }, {});
      analytics.trackEvent(action, label, payload);

      if (DEPLOY_ENV === 'development') {
        console.debug(`[Analytics]`, { featureName, label, payload });
      }
    },
    [analytics.trackEvent, property],
  );

  return { trackProperty };
};

export default useTrackProperty;
